import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  Input,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { AtMessageModule, IconModule, ItemCounterModule, LaunchDialogService, ListNavigationModule, MediaModule, OutletModule, PageComponentModule, PaginationModule, ProductListItemContext, ProductListItemContextSource, ProductListModule, SpinnerModule, StarRatingModule } from '@spartacus/storefront';
import { filter, take, tap } from 'rxjs/operators';
import { UserAccountFacade } from '@spartacus/user/account/root';
import { FeaturesConfigModule, I18nModule, Image, Product, TranslationService, UrlModule, User, WindowRef } from '@spartacus/core';
import { Subscription } from 'rxjs';
import { GenProductService } from 'src/app/generac/services/product.service';
import { GenProductScaleQuantityToPrice } from 'src/app/generac/models/product.model';
import { GenGoogleAnalyticsService } from 'src/app/generac/services/google-analytics.service';
import { TooltipPosition } from 'src/app/generac/enums/tooltip-position.enum';
import { GenUnavailabilityDetailType } from 'src/app/generac/enums/unavailability-detail-type.enum';
import { GenContactFactoryInfo } from 'src/app/generac/models/contact-info.model';
import { GenAddToCartModule } from '../../../cart/product-add-to-cart/product-add-to-cart.module';
import { GenProductPurchaseRestrictionMessageComponent } from '../../product-purchase-restriction-message/product-purchase-restriction-message.component';
import { GenInfoPriceWrapperComponent } from 'src/app/generac/shared/components/info-price-wrapper/info-price-wrapper.component';
import { GenAddToFavoritesComponent } from '../../../favorites/add-to-favorites/add-to-favorites.component';
import { GenSpIconsComponent } from 'src/app/generac/shared/components/sp-icons/sp-icons.component';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { SafeModule } from 'src/app/generac/shared/pipes/safe/safe.module';
import { GenSharedModule } from 'src/app/generac/shared/shared.module';
import { GenInfoMessageComponent } from 'src/app/generac/shared/components/info-message/info-message.component';
import { GenProductBadgeComponent } from '../../../../shared/components/product-badge/product-badge.component';

@Component({
  selector: 'gen-product-list-item',
  templateUrl: './product-list-item.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    ProductListItemContextSource,
    {
      provide: ProductListItemContext,
      useExisting: ProductListItemContextSource,
    },
  ],
  imports: [
    GenAddToCartModule,
    GenInfoMessageComponent,
    GenProductPurchaseRestrictionMessageComponent,
    GenInfoPriceWrapperComponent,
    GenAddToFavoritesComponent,
    GenSpIconsComponent,
    UrlModule,
    RouterModule,
    I18nModule,
    MediaModule,
    AtMessageModule,
    CommonModule,
    FeaturesConfigModule,
    IconModule,
    ItemCounterModule,
    GenSharedModule,
    ListNavigationModule,
    OutletModule,
    PageComponentModule,
    SpinnerModule,
    StarRatingModule,
    SafeModule,
    ProductListModule,
    PaginationModule,
    GenProductBadgeComponent,
  ]
})
export class GenProductListItemComponent implements OnChanges {

  @Input() product: Product;

  @Input() gaProductCategories: { name: string }[];
  @Input() gaListName: string;
  @Input() gaEntrySource: string;

  @Input() set phoneNumberOrEmail(value: string) {
    this.contactForPricingHref = value?.includes('@') ? `mailto: ${value}` : `tel: ${value}`;
    this._phoneNumberOrEmail = value;
  }

  get phoneNumberOrEmail(): string {
    return this._phoneNumberOrEmail;
  }

  @Input() tooltipMessage: string;

  @HostListener('window:resize')
  onResize() {
    this.isMobileView = this.winRef?.nativeWindow.innerWidth < 768;
  }

  callForPricingMessage = '';
  contactForPricingHref: string;
  isMobileView: boolean;
  isServicePartsEnabled = false;
  _phoneNumberOrEmail: string;
  spGearImg: Image = {
    url: 'assets/icons/sp_gear_thumb.svg'
  };
  GenUnavailabilityDetailType = GenUnavailabilityDetailType;
  TooltipPosition = TooltipPosition;
  private user: User;
  private subscription = new Subscription();

  constructor(
    protected productListItemContextSource: ProductListItemContextSource,
    protected googleAnalyticsService: GenGoogleAnalyticsService,
    protected winRef: WindowRef,
    protected translation: TranslationService,
    private productService: GenProductService,
    private userAccount: UserAccountFacade,
    private launchDialogService: LaunchDialogService
  ) {
    this.initListConfig();
    this.gerUser();
  }

  ngOnChanges(changes?: SimpleChanges): void {
    if (changes?.product) {
      this.productListItemContextSource.product$.next(this.product);
    }
  }

  selectItem(item: Product): void {
    const product = {
      ...item,
      categories: this.gaProductCategories,
    };
    this.googleAnalyticsService.sendGaEvent('select_item', {
      items: [this.googleAnalyticsService.mapProductToGaItem(product, 0, undefined, this.gaListName, this.gaListName)],
      item_list_name: this.gaListName || '',
      item_list_id: this.gaListName ? this.gaListName.toLowerCase().trim().replace(/ /g, '_') : '',
      currency: product.price?.currencyIso,
    });
  }

  getDisplayPrice(product: any) {
    const quantity = this.productService.getQuantity(product.code);
    let matchingPrice = this.product.price?.formattedValue;

    if (this.product.scaleQuantityToPrice?.length > 1) {
      const sortedScales = [...this.product.scaleQuantityToPrice].sort(
        (a: GenProductScaleQuantityToPrice, b: GenProductScaleQuantityToPrice) => parseInt(a.scale, 10) - parseInt(b.scale, 10)
      );

      for (let i = 0; i < sortedScales.length; i++) {
        const currentScale = parseInt(sortedScales[i].scale, 10);

        if (quantity >= currentScale) {
          matchingPrice = sortedScales[i].price?.formattedValue;
        } else {
          break;
        }
      }
    }

    return matchingPrice;
  }

  openContactFactory() {
    const contactFactoryInfo: GenContactFactoryInfo = {
      accountNumber: this.user.orgUnit.uid,
      companyName: this.user.orgUnit.name,
      supportPhoneNumber: '1-800-883-7535',
      subject: 'Service Part Contact Request',
      itemNumber: this.product.code,
      itemDescription: this.product.name
    };

    const dialog = this.launchDialogService.openDialog(
      'CONTACT_FACTORY',
      undefined,
      undefined,
      contactFactoryInfo
    );

    if (dialog) {
      dialog.pipe(take(1)).subscribe();
    }
    this.subscription.add(
      this.launchDialogService.dialogClose
        .subscribe()
    );
  }

  private initListConfig() {
    this.isMobileView = this.winRef?.nativeWindow.innerWidth < 768;
    this.isServicePartsEnabled = JSON.parse(this.winRef.localStorage.getItem('isServicePartsEnabled'));
  }

  private gerUser() {
    this.subscription.add(
      this.userAccount.get()
        .pipe(
          filter(Boolean),
          tap(user => this.user = user)
        ).subscribe()
    );
  }
}
